import EmblaCarousel from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import AutoScroll from "embla-carousel-auto-scroll";
import { createTimeline, stagger } from "animejs";
import "glightbox/dist/css/glightbox.min.css";
import { mq } from "./modules/utils";
import { initAccordions, syncAccordionHeights, enhanceCmsAccordions } from "./modules/_accordion";
import { initSliders } from "./modules/_slider";
import { initModals } from "./modules/_modal";
import ScrollHint from "scroll-hint";
import "scroll-hint/css/scroll-hint.css";

const BODY = document.getElementsByTagName("body");

const injectSubPageSidenav = async () => {
  if (document.body?.id === "top-page") return;
  if (document.querySelector(".sidenav")) return;

  try {
    // news/*.html 等「inc と同じ階層の子ディレクトリ」からは、ページ URL 基準の ../inc/ が常に正しい
    const navUrl = new URL("../inc/nav.html", window.location.href).toString();
    const response = await fetch(navUrl, { credentials: "same-origin" });
    if (!response.ok) return;

    const navMarkup = (await response.text()).trim();
    if (!navMarkup) return;

    const header = document.querySelector(".global-header");
    if (header) {
      header.insertAdjacentHTML("afterend", navMarkup);
    } else {
      document.body.insertAdjacentHTML("afterbegin", navMarkup);
    }
  } catch (error) {
    console.error("Failed to inject sidenav:", error);
  }
};

const injectSubPageFooterNav = async () => {
  if (document.body?.id === "top-page") return;

  const mount = document.querySelector(".js-footer-nav-mount");
  if (!mount) return;
  if (mount.parentElement?.querySelector(".site-footer__nav")) return;

  try {
    const navUrl = new URL("../inc/nav-footer.html", window.location.href).toString();
    const response = await fetch(navUrl, { credentials: "same-origin" });
    if (!response.ok) return;

    const navMarkup = (await response.text()).trim();
    if (!navMarkup) return;

    const template = document.createElement("template");
    template.innerHTML = navMarkup;
    const node = template.content.firstElementChild;
    if (node) {
      mount.replaceWith(node);
    }
  } catch (error) {
    console.error("Failed to inject footer nav:", error);
  }
};

const injectSubPageGlobalNav = async () => {
  if (document.body?.id === "top-page") return;

  const mount = document.querySelector(".js-global-nav-mount");
  if (!mount) return;
  if (mount.parentElement?.querySelector(".global-nav")) return;

  try {
    const navUrl = new URL("../inc/global-nav.html", window.location.href).toString();
    const response = await fetch(navUrl, { credentials: "same-origin" });
    if (!response.ok) return;

    const navMarkup = (await response.text()).trim();
    if (!navMarkup) return;

    const template = document.createElement("template");
    template.innerHTML = navMarkup;
    const node = template.content.firstElementChild;
    if (node) {
      mount.replaceWith(node);
    }
  } catch (error) {
    console.error("Failed to inject global nav:", error);
  }
};

const hbgFunc = () => {
  const sidenav = document.querySelector(".sidenav");
  const bg = document.querySelector(".bg");
  const triggers = document.querySelectorAll(".js-hbg");
  const sidenavLinks = document.querySelectorAll(".sidenav-link");
  const hbgButton = document.querySelector(".fc-header__hbg, .hbg-btn");

  if (!sidenav || !bg || !triggers.length) return;

  // 開いた瞬間、同じ座標の閉じるボタンが同一タップを受け取って即閉じるのを防ぐ
  let clickLock = false;

  const lockClicks = () => {
    clickLock = true;
    BODY[0].classList.add("hbg-opening");
    window.setTimeout(() => {
      clickLock = false;
      BODY[0].classList.remove("hbg-opening");
    }, 400);
  };

  const syncExpanded = (open) => {
    if (!hbgButton) return;
    hbgButton.setAttribute("aria-expanded", open ? "true" : "false");
    hbgButton.setAttribute("aria-label", open ? "メニューを閉じる" : "メニューを開く");
  };

  const animateLinks = (opening, onDone) => {
    const links = sidenav.querySelectorAll("ul a,.gnav__foot a,.gnav__sponsor");
    if (!links.length) {
      onDone?.();
      return;
    }

    const tl = createTimeline({
      defaults: {
        ease: "inOut(3)",
        duration: 300,
      },
    });

    if (opening) {
      tl.set(links, {
        translateY: "10px",
        opacity: 0,
      });
    }

    tl.add(
      links,
      {
        translateY: opening ? ["10px", 0] : ["0", "10px"],
        opacity: opening ? [0, 1] : [1, 0],
        ease: "inOut(3)",
        duration: 400,
        delay: stagger(40, { start: -30 }),
        onComplete: () => onDone?.(),
      },
      opening ? "+=300" : 0,
    );
  };

  const openMenu = () => {
    if (BODY[0].classList.contains("hbg-open") || clickLock) return;

    lockClicks();
    bg.classList.add("bg-open");
    BODY[0].classList.add("hbg-open");
    syncExpanded(true);
    animateLinks(true);
  };

  const closeMenu = () => {
    if (!BODY[0].classList.contains("hbg-open") || clickLock) return;
    if (BODY[0].classList.contains("close-sidenav")) return;

    lockClicks();
    BODY[0].classList.add("close-sidenav");
    syncExpanded(false);
    animateLinks(false, () => {
      BODY[0].classList.remove("hbg-open");
      window.setTimeout(() => {
        bg.classList.remove("bg-open");
        BODY[0].classList.remove("close-sidenav");
      }, 300);
    });
  };

  triggers.forEach((el) => {
    el.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      const closes = el.classList.contains("gnav__close") || el.classList.contains("bg");
      if (closes) {
        closeMenu();
        return;
      }

      if (BODY[0].classList.contains("hbg-open")) {
        closeMenu();
      } else {
        openMenu();
      }
    });
  });

  sidenavLinks.forEach((link) => {
    link.addEventListener("click", () => {
      if (!mq("sp")) return;
      closeMenu();
    });
  });
};

/**
 * ハッシュ付きで開かれたとき／ページ内リンクをクリックしたときに対象へスクロールする
 * 下方向（ヘッダー退避）：コンテンツ先頭ぴったり
 * 上方向（ヘッダー再表示）：ヘッダーの高さ分だけ下げる
 * location.hash 代入はネイティブジャンプと二重になるので使わない。
 */
const hashScrollFunc = () => {
  const getOffset = (header, scrollingDown) => {
    if (header?.classList.contains("fc-header")) {
      return scrollingDown ? 0 : header.offsetHeight;
    }

    if (header?.classList.contains("global-header") && !mq("sp")) {
      return header.offsetHeight;
    }

    return 0;
  };

  const scrollToHash = (hash) => {
    if (!hash || hash === "#") return;

    const target = document.querySelector(hash);
    if (!target) return;

    const header = document.querySelector(".fc-header") || document.querySelector(".global-header");
    const targetTop = target.getBoundingClientRect().top + window.scrollY;
    const scrollingDown = targetTop > window.scrollY + 1;

    if (header?.classList.contains("fc-header") && !BODY[0].classList.contains("hbg-open")) {
      header.classList.toggle("is-hidden", scrollingDown);
    }

    const top = Math.max(0, targetTop - getOffset(header, scrollingDown));
    window.scrollTo({ top, behavior: "smooth" });
  };

  const handleHashScroll = () => {
    scrollToHash(window.location.hash);
  };

  document.addEventListener("click", (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const hash = link.getAttribute("href");
    if (!hash || hash === "#") return;
    if (!document.querySelector(hash)) return;

    event.preventDefault();
    scrollToHash(hash);

    if (window.location.hash !== hash) {
      history.pushState(null, "", hash);
    }
  });

  window.addEventListener("hashchange", handleHashScroll);

  // 別ページからの ./#id など。画像・レイアウト確定後にスクロールする
  if (!window.location.hash) return;

  const scrollWhenReady = () => {
    requestAnimationFrame(handleHashScroll);
  };

  if (document.readyState === "complete") {
    scrollWhenReady();
  } else {
    window.addEventListener("load", scrollWhenReady, { once: true });
  }
};

/**
 * ヒーローが画面から見えなくなったら .js-hbg に .is-open を追加
 */
const observeKV = () => {
  const kv = document.querySelector(".hero");
  const hbgBtn = document.querySelector(".js-hbg");

  if (!kv || !hbgBtn) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          hbgBtn.classList.add("is-open");
        } else {
          hbgBtn.classList.remove("is-open");
        }
      });
    },
    {
      root: null,
      rootMargin: "0px",
      threshold: 0,
    },
  );

  observer.observe(kv);
};

/**
 * ヘッダー：下スクロールで枠外へ退避、上スクロールでスライド表示
 * CTA：メインビジュアルから少し下に来たら追従表示
 * scroll は passive、実処理は rAF 1本。読む値は scrollY のみ。
 */
const initChromeScroll = () => {
  const header = document.querySelector(".fc-header");
  const cta = document.querySelector(".fc-cta");
  if (!header && !cta) return;

  const IGNORE_DELTA = 8;
  const CTA_OFFSET = 80;
  let lastY = window.scrollY;
  let ticking = false;
  let ctaVisible = null;

  const apply = () => {
    ticking = false;
    // モーダル開閉で body class が変わるとヘッダー／CTA が追従判定してガクつく
    if (BODY[0].classList.contains("glightbox-open")) return;

    const y = Math.max(0, window.scrollY);
    const delta = y - lastY;
    const menuOpen = BODY[0].classList.contains("hbg-open");

    if (header) {
      let hide = header.classList.contains("is-hidden");
      if (menuOpen || y <= 0) {
        hide = false;
      } else if (Math.abs(delta) >= IGNORE_DELTA) {
        hide = delta > 0;
      }

      header.classList.toggle("is-hidden", hide);
    }

    if (cta) {
      const visible = y > CTA_OFFSET;
      if (visible !== ctaVisible) {
        ctaVisible = visible;
        cta.classList.toggle("is-visible", visible);
        cta.setAttribute("aria-hidden", visible ? "false" : "true");
        cta.inert = !visible;
      }
    }

    lastY = y;
  };

  const schedule = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(apply);
  };

  window.addEventListener("scroll", schedule, { passive: true });

  const observer = new MutationObserver(schedule);
  observer.observe(BODY[0], { attributes: true, attributeFilter: ["class"] });

  apply();
};

const syncScheduleHeightToPhotoStrip = () => {
  const photoStrip = document.querySelector(".photo-strip");
  const scheduleSection = document.getElementById("schedule");

  if (!photoStrip || !scheduleSection) return;

  const updateHeight = () => {
    const scheduleHeight = Math.round(scheduleSection.getBoundingClientRect().height);
    const photoStripItemCount = photoStrip.querySelectorAll(".photo-strip__item:not([data-clone])").length;
    photoStrip.style.setProperty("--schedule-heigh", `${scheduleHeight}px`);
    photoStrip.style.setProperty("--photo-strip-item-count", String(photoStripItemCount));
  };

  updateHeight();
  window.addEventListener("resize", updateHeight);
  window.addEventListener("load", updateHeight);
};

/** アコーディオンの高さ実測（実装は modules/_accordion.js） */
const syncFaqAnswerHeight = () => syncAccordionHeights(document);

const syncTeamIntroQuoteWidth = () => {
  const quotes = document.querySelectorAll(".team-intro__quote");
  if (!quotes.length) return;

  quotes.forEach((quote) => {
    const content = quote.querySelector("p > span") || quote.querySelector("p");
    if (!content) return;

    const contentWidth = Math.ceil(content.getBoundingClientRect().width);
    quote.style.setProperty("--width", `${contentWidth}`);
  });
};

/** .cms-content 内 summary へのトグルアイコン注入（実装は modules/_accordion.js） */
const appendFaqToggleToSummaries = () => enhanceCmsAccordions(document);

const initializeHeroSlider = () => {
  const emblaNode = document.querySelector(".hero__slider.embla");
  if (!emblaNode) return;

  const viewportNode = emblaNode.querySelector(".embla__viewport");
  const dotsRoot = emblaNode.querySelector(".hero__slider-dots");
  if (!viewportNode || !dotsRoot) return;

  const autoplay = Autoplay({
    delay: 3000,
    stopOnInteraction: false,
  });

  const embla = EmblaCarousel(
    viewportNode,
    {
      loop: true,
      align: "start",
      dragFree: false,
      containScroll: "trimSnaps",
    },
    [autoplay],
  );

  const createDots = () => {
    const scrollSnaps = embla.scrollSnapList();
    dotsRoot.innerHTML = "";

    scrollSnaps.forEach((_, index) => {
      const dotButton = document.createElement("button");
      dotButton.className = "hero__dot";
      dotButton.type = "button";
      dotButton.setAttribute("role", "tab");
      dotButton.setAttribute("aria-selected", "false");
      dotButton.setAttribute("aria-label", `スライド${index + 1}`);
      dotButton.addEventListener("click", () => embla.scrollTo(index));
      dotsRoot.appendChild(dotButton);
    });
  };

  const updateSelectedDot = () => {
    const dots = dotsRoot.querySelectorAll(".hero__dot");
    const selectedIndex = embla.selectedScrollSnap();
    dots.forEach((dot, index) => {
      const isActive = index === selectedIndex;
      dot.classList.toggle("hero__dot--active", isActive);
      dot.setAttribute("aria-selected", isActive ? "true" : "false");
    });
  };

  embla.on("reInit", createDots);
  embla.on("reInit", updateSelectedDot);
  embla.on("select", updateSelectedDot);

  createDots();
  updateSelectedDot();
};

const initializePhotoStrip = () => {
  const emblaNode = document.querySelector(".photo-strip__inner.embla");
  if (!emblaNode) return;

  const viewportNode = emblaNode.querySelector(".embla__viewport");
  const containerNode = emblaNode.querySelector(".embla__container");
  if (!viewportNode || !containerNode) return;

  const slides = [...containerNode.querySelectorAll(".embla__slide")];
  slides.forEach((slide) => {
    const clone = slide.cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    clone.dataset.clone = "true";
    containerNode.appendChild(clone);
  });

  const autoScroll = AutoScroll({
    speed: 1.2,
    startDelay: 0,
    stopOnInteraction: false,
    stopOnMouseEnter: true,
    stopOnFocusIn: false,
  });

  const embla = EmblaCarousel(
    viewportNode,
    {
      loop: true,
      dragFree: true,
      align: "start",
      containScroll: false,
    },
    [autoScroll],
  );

};

const initScrollHint = () => {
  if (!mq("pc")) return;

  const targets = document.querySelectorAll(".js-scrollable");
  if (!targets.length) return;

  new ScrollHint(".js-scrollable", {
    remainingTime: 5000,
    i18n: {
      scrollable: "スクロールできます",
    },
  });
};

const initializePage = async () => {
  if (BODY[0].classList.contains("open")) return;
  await Promise.all([injectSubPageSidenav(), injectSubPageFooterNav(), injectSubPageGlobalNav()]);
  appendFaqToggleToSummaries();
  syncScheduleHeightToPhotoStrip();
  syncFaqAnswerHeight();
  syncTeamIntroQuoteWidth();
  initializeHeroSlider();
  initializePhotoStrip();

  // 汎用コンポーネント（対象のマークアップが無いページでは何もしない）
  initAccordions(document);
  initSliders(document);
  initModals(document);
  initScrollHint();

  BODY[0].classList.add("open");
  hashScrollFunc();
  hbgFunc();
  observeKV();
  initChromeScroll();
};

document.addEventListener("DOMContentLoaded", initializePage);
window.addEventListener("load", syncScheduleHeightToPhotoStrip);
window.addEventListener("load", syncFaqAnswerHeight);
window.addEventListener("load", syncTeamIntroQuoteWidth);
window.addEventListener("resize", syncFaqAnswerHeight);
window.addEventListener("resize", syncTeamIntroQuoteWidth);
window.addEventListener("resize", appendFaqToggleToSummaries);
